import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import usePageMeta from "../hooks/usePageMeta";
import HomeHero, { heroBtnFilled, heroBtnOutline } from "../components/HomeHero";
import { local } from "../lib/images";
import { api } from "../utils/api";
import { fetchOrcidWorks, mergePublications, isValidOrcid } from "../lib/orcid";

/**
 * IWM's own photography. Every one of these was a stock stand-in until the group
 * supplied the real thing; the notes on each say what it actually shows, so a
 * caption can never drift away from the picture again.
 */
// The disciplines the group works across — the scrolling band under the opening.
const DISCIPLINES = ["Engineering", "Agriculture", "Environmental Science", "Soils", "Data", "Policy"];

const IMAGES = {
  // The barley plots: strip trials running away from the camera, pink treatment
  // flags down the headland, the campus treeline behind. This replaced the corn
  // shot, which was two-thirds sky and whose greens all sat at the same value —
  // under the hero's 30% desaturation the whole frame collapsed into one tone.
  // Here the ripe gold and the standing green stay a long way apart, and the
  // horizon sits a third of the way down instead of halfway.
  land: {
    ...local("iwm-fieldplots"),
    alt: "Barley strip trials at the Agricultural Campus, treatment flags marking the plot edges",
  },
  // Green crab going into a compost bay — an invasive species turned into a
  // feedstock. Difficult-feedstock composting is the group's core subject, and
  // this is three of them actually doing it.
  group: {
    ...local("iwm-crab"),
    alt: "Researchers loading green crab into a wooden compost bay",
  },
  // An automated flux chamber with its solar-powered logger, sitting in a cereal
  // plot: greenhouse gas measured where the amendment was applied.
  streams: {
    ...local("iwm-flux"),
    alt: "An automated gas flux chamber and solar-powered logger in a cereal research plot",
  },
  // Corn silage coming off the plots into the forage wagon.
  // The lab door itself: the brass plaque, the safety board, and the framed
  // "compost: proof that there is life after death" that someone hung beside it.
  samples: {
    ...local("iwm-labdoor"),
    alt: "The Innovative Waste Management lab door, with its brass plaque and safety signage",
  },
};

// A snapshot of the story in numbers.
//
// Derived from the live data, not typed in. Two of these were simply wrong when
// they were static: "19 researchers" against a roster of 25 current members, and
// "5 active projects" against 4 ongoing and 1 completed. Hardcoded counts drift
// the moment somebody is added in the admin, so the fallbacks below are only
// what shows before the API answers — the figures on screen come from the same
// endpoints the rest of the site reads.
const STAT_FALLBACK = { publications: 43, members: 25, projects: 4 };

// The whole of the rest of the site, offered once, in one place. The individual
// pages are not previewed here — that repetition is what this page is avoiding.
const LEARN_MORE = [
  { to: "/team", label: "Our Team", body: "The people, research interests and expertise that make up IWM." },
  { to: "/projects", label: "Project Stories", body: "What we are working on, who is involved, and who funds it." },
  { to: "/publications", label: "Selected Publications", body: "Nearly two decades of research across waste, soils and systems." },
  { to: "/contact", label: "Connect with Us", body: "Collaborations, enquiries and community engagement." },
];

export default function Home() {
  const { settings: s } = useSettings();
  usePageMeta(
    "Our Story",
    "The Innovative Waste Management Research Group is a transdisciplinary lab working at the intersection of waste management and the circular economy."
  );
  const locationLabel = [s.city, s.region].filter(Boolean).join(" · ");

  // The four figures in the strip, counted from the same endpoints the rest of
  // the site reads. Failure is silent and leaves the fallbacks in place: a
  // slightly stale number is better than a dash, and far better than blocking
  // the landing page on three requests it does not otherwise need.
  const [counts, setCounts] = useState(STAT_FALLBACK);
  const orcidId = String(s.orcid_id || "").trim();

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      const [team, projects, pubs] = await Promise.all([
        api.get("/team").then((r) => r.data).catch(() => null),
        api.get("/projects").then((r) => r.data).catch(() => null),
        api.get("/publications").then((r) => r.data).catch(() => null),
      ]);
      if (cancelled) return;

      const isAlumni = (m) => m?.alumni === true || m?.alumni === 1 || m?.alumni === "true";
      const next = { ...STAT_FALLBACK };
      if (Array.isArray(team)) next.members = team.filter((m) => !isAlumni(m)).length;
      // "Active" means ongoing — a completed project is still in the register
      // but it is not something the group is working on now.
      if (Array.isArray(projects)) {
        next.projects = projects.filter((x) => /ongoing/i.test(String(x?.status || ""))).length;
      }
      if (Array.isArray(pubs)) next.publications = pubs.length;
      setCounts(next);

      // Then top the publication count up with ORCID, so it matches what the
      // publications page actually lists.
      if (Array.isArray(pubs) && isValidOrcid(orcidId)) {
        try {
          const works = await fetchOrcidWorks(orcidId, { signal: controller.signal });
          if (!cancelled && works.length) {
            setCounts((c) => ({ ...c, publications: mergePublications(pubs, works).length }));
          }
        } catch { /* keep the curated count */ }
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [orcidId]);

  const stats = useMemo(() => [
    { k: "2007", v: "Established at Dalhousie" },
    // "on record" rather than "peer-reviewed": the list is Dr. Price's
    // publication record and not every entry has been verified as peer-reviewed.
    { k: String(counts.publications), v: "Publications on record" },
    { k: String(counts.members), v: "Researchers and students" },
    { k: String(counts.projects), v: "Active research projects" },
  ], [counts]);

  return (
    <div data-testid="home-page">
      <HomeHero
        image={IMAGES.land.src}
        imageSrcSet={IMAGES.land.srcSet}
        imageAlt={IMAGES.land.alt}
        eyebrow="Research group · Est. 2007"
        meta={
          locationLabel && (
            <span
              className="inline-flex items-center gap-1.5 border border-white/25 px-2.5 py-1 text-[10px] font-mono tracking-widest uppercase text-[#F9F8F6] bg-white/5 backdrop-blur-sm"
              data-testid="hero-location"
            >
              <MapPin size={10} className="text-[#A17E3B]" /> {locationLabel}
            </span>
          )
        }
        title={
          <>
            Waste is not the end of the system.{" "}
            <span className="block mt-2 text-[#D1CEC7]">
              At{" "}
              <span className="relative inline-block text-[#F9F8F6]">
                IWM
                <span className="absolute left-0 -bottom-1 h-[3px] w-full bg-[#A17E3B]" />
              </span>
              , it&apos;s where we start.
            </span>
          </>
        }
        lede="The Innovative Waste Management Research Group is a transdisciplinary lab working at the intersection of waste management and the circular economy."
        actions={
          <>
            <Link to="/projects" className={heroBtnFilled} data-testid="hero-cta-projects">
              Read the project stories <ArrowRight size={14} />
            </Link>
            <Link to="/team" className={heroBtnOutline} data-testid="hero-cta-team">
              Meet the group
            </Link>
          </>
        }
      >
        {/* Stats, riding the base of the photograph rather than forming their
            own band further down the page. */}
        <div className="relative border-t border-white/15 backdrop-blur-[2px] bg-[#1C2722]/30">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/15">
            {stats.map((stat) => (
              <div key={stat.v} className="py-6 lg:py-7 px-4 lg:px-6 first:pl-0">
                <div className="font-serif text-3xl lg:text-[40px] leading-none text-[#F9F8F6]" data-testid="home-stat-value">
                  {stat.k}
                </div>
                <div className="overline mt-2 !text-[#D1CEC7]">{stat.v}</div>
              </div>
            ))}
          </div>
        </div>
      </HomeHero>

      {/* Disciplines ticker — its own band now, at full width, so it reads as a
          seam between the opening and the story rather than page furniture. */}
      <section className="border-b hairline bg-[#F2EFEA] overflow-hidden" data-testid="disciplines-section">
        {/* Two identical copies; each carries its own trailing gap so the
            -50% translate lands exactly one copy over and the loop is seamless. */}
        <div className="flex ticker whitespace-nowrap font-serif text-2xl lg:text-3xl text-[#1C2722] py-6" aria-hidden="true">
          {[...Array(2)].map((_, n) => (
            <div key={n} className="flex gap-14 pr-14">
              {DISCIPLINES.map((d) => (
                <React.Fragment key={d}>
                  <span>{d}</span>
                  <span className="text-[#A17E3B]">·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* WHO WE ARE — an editorial spread rather than a heading in a narrow
          column beside a block of text. That layout left a column of white as
          tall as the whole passage, because the heading was three lines and the
          text was thirty. The opening paragraph now runs wide at display size,
          the body flows into two measures beneath it, and the closing line is
          pulled out as the statement it actually is. */}
      <section className="border-b hairline" data-testid="story-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <figure className="border hairline overflow-hidden media-zoom reveal">
            <img
              src={IMAGES.group.src}
              srcSet={IMAGES.group.srcSet}
              sizes="(min-width: 1440px) 1360px, 100vw"
              alt={IMAGES.group.alt}
              loading="lazy"
              decoding="async"
              className="w-full aspect-[5/2] object-cover object-center grayscale-[20%] contrast-[0.98]"
            />
            <figcaption className="border-t hairline px-5 py-3 overline bg-[#F2EFEA]">
              Green crab into the compost bay — an invasive species handled as a feedstock
            </figcaption>
          </figure>

          {/* Heading and lead share the row with a tall frame — the right half
              was empty white before, which is what made this section read as
              unfinished rather than spacious. */}
          <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-6 reveal">
              <div className="overline mb-5">§ 01 · Our Story</div>
              <h2 className="font-serif text-3xl md:text-5xl lg:text-[52px] leading-[1.04] text-[#1C2722] tracking-tight max-w-[19ch]">
                Where the work started, and how it has grown.
              </h2>
              <p className="mt-10 font-serif text-xl md:text-[25px] leading-[1.45] text-[#1C2722]">
                Established at Dalhousie University&apos;s Agricultural Campus in 2007, IWM began by
                exploring better ways of understanding, managing, and making use of waste.
              </p>
            </div>
            <figure className="lg:col-span-6 border hairline overflow-hidden media-zoom reveal lg:h-full lg:flex lg:flex-col">
              {/* The image is absolutely positioned inside this box on purpose.
                  Left in normal flow it contributes its own 2:3 intrinsic height
                  to the flex basis, which pushed the row to 811px and made the
                  gap under the lead paragraph worse, not better — min-h-0 does
                  not prevent that. Taken out of flow it contributes nothing, so
                  the row is sized by the text column alone and the picture fills
                  whatever height that leaves. */}
              <div className="relative w-full h-[320px] lg:h-auto lg:flex-1 lg:min-h-0">
                <img
                  src={IMAGES.streams.src}
                  srcSet={IMAGES.streams.srcSet}
                  sizes="(min-width: 1024px) 46vw, 100vw"
                  alt={IMAGES.streams.alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover grayscale-[25%] contrast-[0.97]"
                />
              </div>
              <figcaption className="border-t hairline px-4 py-2.5 overline bg-[#F2EFEA]">
                Measuring what the soil gives back
              </figcaption>
            </figure>
          </div>

          {/* Body, in two measures. CSS columns rather than a grid: the text is
              one continuous passage and should flow, not be split by hand into
              chunks that go out of balance the moment the copy is edited. */}
          <div className="mt-10 text-[16.5px] leading-relaxed text-[#4A5A52] lg:columns-2 lg:gap-14 reveal">
            <p className="mb-6">
              Since then, the group has grown under the leadership of Dr. Gordon Price. IWM brings
              together researchers with different disciplinary backgrounds and different ways of
              approaching environmental problems. Under Gordon&apos;s mentorship, our work moves from
              the laboratory to field plots, into industry and community settings, and back to the
              desk to examine policy, organizations, data, and systems.
            </p>
            <p className="mb-6">
              That range is intentional. Waste is rarely only a technical problem. IWM brings these
              different perspectives together to explore and better understand sustainable and
              circular systems.
            </p>
            <p className="mb-0">
              We see exploration as an important part of innovation: asking different questions,
              testing new approaches, and being willing to investigate problems that are complex,
              unusual, or unresolved.
            </p>
          </div>

          {/* The closing goal, on a rule, with the second frame alongside it. */}
          <div className="mt-14 lg:mt-20 border-t-2 border-[#1C2722] pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <blockquote className="lg:col-span-7 reveal">
              <p className="font-serif text-2xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight text-[#1C2722]">
                At the centre of that work is a shared goal: to develop useful knowledge and
                practical possibilities that help move waste management toward{" "}
                <em className="not-italic text-[#A17E3B]">a more circular economy</em>.
              </p>
            </blockquote>
            <figure className="lg:col-span-5 border hairline overflow-hidden media-zoom reveal">
              <img
                src={IMAGES.samples.src}
                srcSet={IMAGES.samples.srcSet}
                sizes="(min-width: 1024px) 40vw, 100vw"
                alt={IMAGES.samples.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-[300px] lg:h-[400px] object-cover object-[center_35%] grayscale-[25%] contrast-[0.97]"
              />
              <figcaption className="border-t hairline px-4 py-2.5 overline bg-[#F2EFEA]">
                Cox Institute, Room 132A
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* LEARN MORE */}
      <section className="reveal" data-testid="learn-more-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline mb-4">§ 02 · Learn more</div>
          <h2 className="font-serif text-3xl md:text-4xl leading-[1.1] text-[#1C2722] tracking-tight max-w-2xl">
            The rest of the story is on four pages.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 border-t border-l hairline">
            {LEARN_MORE.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group relative p-8 lg:p-10 border-r border-b hairline hover:bg-[#F2EFEA] transition-colors duration-200"
                data-testid={`learn-more-${item.to.replace("/", "")}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight text-[#1C2722] tracking-tight group-hover:text-[#A17E3B] transition-colors">
                    {item.label}
                  </h3>
                  <ArrowRight
                    size={20}
                    strokeWidth={1.5}
                    className="text-[#A17E3B] shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </div>
                <p className="mt-3 text-[15px] text-[#4A5A52] leading-relaxed">{item.body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
